import {
  Button,
  Flex,
  Form,
  Input,
  Select,
  Space,
  TimePicker,
  Typography,
  Collapse,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import React from 'react';

import { CustomInputNumber, MultipleSelect } from '@app/components';
import { OFF, ONE_TIME_CODE, PREFIX, RULES_IS_REQUIRE } from '@app/constants';
import {
  useAiSettingsTab,
  useAiSettingsTabEffect,
} from '@app/pages/Users/EditUser/components/AiSettingsTab/hooks';
import {
  TONE_SELECT_OPTIONS,
  LLM_PROVIDER_SELECT_OPTIONS,
} from '@app/pages/Users/EditUser/data';

import '@pages/Users/EditUser/components/AiSettingsTab/styles/AiSettingsTab.desktop.scss';
import type { Dayjs } from 'dayjs';

const { Text } = Typography;

export const AiSettingsTab: React.FC = () => {
  const { isDataLoading, data, allBranches } = useAiSettingsTabEffect();
  const {
    form,
    handleFinishForm,
    isUpdating,
    handleGetDefaultProfile,
    validateJson,
    handleTimeChange,
    disabledTime,
    selectedBranches,
    setSelectedBranches,
  } = useAiSettingsTab({ data, allBranches });

  const MIN_CUSTOM_DEADLINE_DAYS = 0;
  const MAX_CUSTOM_DEADLINE_DAYS = 180;

  const validateNumericInput = (value: string | undefined) => {
    if (value === undefined || value === null || value === '') {
      return Promise.resolve();
    }

    if (!/^\d+$/.test(value)) {
      return Promise.reject(new Error('Введите целое число'));
    }

    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return Promise.reject(new Error('Введите корректное число'));
    }

    if (
      numericValue < MIN_CUSTOM_DEADLINE_DAYS ||
      numericValue > MAX_CUSTOM_DEADLINE_DAYS
    ) {
      return Promise.reject(
        new Error(
          `Введите число от ${MIN_CUSTOM_DEADLINE_DAYS} до ${MAX_CUSTOM_DEADLINE_DAYS}`
        )
      );
    }

    return Promise.resolve();
  };

  return (
    <Form
      form={form}
      initialValues={{
        remember: true,
      }}
      autoComplete={OFF}
      className={`${PREFIX}ai-settings-tab bc-form`}
      onFinish={handleFinishForm}
    >
      <Space
        direction="vertical"
        size="middle"
        className="form-space"
        style={{ width: '100%' }}
      >
        <Flex vertical gap={16} className="flex-container">
          <div>
            <Text strong className="label label-require">
              Инструкция для бота
            </Text>
            <br />
            <Text type="secondary">
              Здесь необходимо указать: как зовут ИИ-агента, как общаться, какие
              эмодзи, воронку продаж по шагам, правила. Это единственное поле,
              которое реально влияет на поведение ИИ-агента в диалоге.
            </Text>
          </div>
          <Form.Item name="OperatorProfile" rules={RULES_IS_REQUIRE}>
            <TextArea
              data-testid="name-input"
              autoComplete={ONE_TIME_CODE}
              size="large"
              rows={10}
              disabled={isDataLoading}
              maxLength={30000}
            />
          </Form.Item>
          <Button
            className="action-button"
            color="primary"
            variant="outlined"
            onClick={handleGetDefaultProfile}
          >
            Вставить шаблон
          </Button>
        </Flex>
        <div className="container message-container">
          <div>
            <Text strong className="label label-require">
              Ограничение длины ответа бота в предложениях
            </Text>
            <br />
            <Text type="secondary">
              5 = развёрнуто, но не многословно. 2-3 = коротко
            </Text>
          </div>
          <Form.Item
            name="MaxSentences"
            rules={RULES_IS_REQUIRE}
            className="mg-0 second-column"
          >
            <CustomInputNumber
              inputValue={form.getFieldValue('MaxSentences')}
              isAllowed={(floatValue: number) =>
                floatValue >= 1 && floatValue <= 20
              }
              disabled={isDataLoading}
              onSuccess={({ value }) => {
                form.setFieldValue('MaxSentences', value);
              }}
              allowDecimal={false}
              placeholder="Введите число от 1 до 20"
            />
          </Form.Item>
        </div>
        <div className="container message-container">
          <div>
            <Text strong className="label label-require">
              Общий тон общения
            </Text>
          </div>
          <Form.Item
            name="Tone"
            rules={RULES_IS_REQUIRE}
            className="mg-0 second-column"
          >
            <Select
              options={TONE_SELECT_OPTIONS}
              placeholder="Выберите тон ответа"
            />
          </Form.Item>
        </div>
        <div className="container message-container">
          <div>
            <Text strong className="label">
              Финальная фраза каждого сообщения
            </Text>
            <br />
            <Text type="secondary">
              Например "Записать вас?". Если пусто — бот сам решает
            </Text>
          </div>
          <Form.Item name="MandatoryCTA" className="mg-0 second-column">
            <Input />
          </Form.Item>
        </div>
        <Collapse
          items={[
            {
              key: 'additional',
              label: 'Дополнительно',
              children: (
                <div className="container message-container">
                  <div>
                    <Text strong>Шаблоны технических сообщений бота</Text>
                    <br />
                    <Text type="secondary">
                      JSON-словарь шаблонов технических сообщений бота
                      (подтверждение записи, нет свободных слотов, ошибка). Если
                      поле пустое — используются значения по умолчанию. Изменять
                      только при тонкой настройке.
                    </Text>
                  </div>

                  <Form.Item
                    name="MessageTemplatesJson"
                    className="mg-0 second-column"
                    rules={[
                      {
                        validator: validateJson,
                      },
                    ]}
                  >
                    <TextArea
                      rows={6}
                      placeholder="Введите JSON со своими шаблонами сообщений"
                    />
                  </Form.Item>
                </div>
              ),
            },
          ]}
        />
        <div className="container message-container">
          <Text strong className="label label-require" data-testid="name">
            Время ожидания окончания диалога
          </Text>
          <Flex className="mg-0 second-column" gap="8px">
            <Form.Item
              name="EndDialogDays"
              rules={[
                ...RULES_IS_REQUIRE,
                {
                  validator: (_, value) => validateNumericInput(value),
                },
              ]}
              className="mg-0"
            >
              <Input
                type="text"
                className="custom_deadline_day_input"
                placeholder="от 0 до 180"
                maxLength={3}
                inputMode="numeric"
                pattern="\d*"
                disabled={isDataLoading}
              />
            </Form.Item>
            <Form.Item
              name="EndDialogTime"
              rules={RULES_IS_REQUIRE}
              className="mg-0"
            >
              <TimePicker disabled={isDataLoading} />
            </Form.Item>
          </Flex>
        </div>
        <div className="container message-container">
          <Text strong className="label label-require" data-testid="name">
            Период группировки сообщений
          </Text>
          <Flex className="mg-0 second-column" gap="8px">
            <Form.Item
              name="GroupingTime"
              rules={[
                ...RULES_IS_REQUIRE,
                {
                  validator: async (_, value: Dayjs) => {
                    if (!value) return Promise.resolve();
                    const m = value.minute();
                    const s = value.second();
                    if (m > 10 || (m === 10 && s > 0)) {
                      return Promise.reject(new Error('Максимум 10:00'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              className="mg-0 second-column"
            >
              <TimePicker
                disabled={isDataLoading}
                format="mm:ss"
                disabledTime={disabledTime}
                onChange={handleTimeChange}
                hideDisabledOptions={true}
              />
            </Form.Item>
          </Flex>
        </div>
        <div className="container message-container">
          <Text strong className="label label-require" data-testid="name">
            Llm провайдер
          </Text>
          <Form.Item
            name="LlmProvider"
            rules={RULES_IS_REQUIRE}
            className="mg-0 second-column"
          >
            <Select
              disabled={isDataLoading}
              options={LLM_PROVIDER_SELECT_OPTIONS}
            />
          </Form.Item>
        </div>
        <div className="container message-container">
          <Text strong className="label label-require" data-testid="name">
            Llm модель
          </Text>
          <Form.Item
            name="LlmModel"
            rules={RULES_IS_REQUIRE}
            className="mg-0 second-column"
          >
            <Input disabled={isDataLoading} />
          </Form.Item>
        </div>
        <div className="container message-container">
          <Text strong className="label label-require">
            Филиалы
          </Text>
          <MultipleSelect
            className="mg-0 second-column"
            placeholder="Все филиалы"
            allOptions={allBranches}
            width={'100%'}
            selectedOption={selectedBranches}
            setSelectedOptions={setSelectedBranches}
          />
        </div>
        <Button
          className="save-button"
          size="large"
          type="primary"
          htmlType="submit"
          loading={isUpdating}
          disabled={isDataLoading}
        >
          Сохранить
        </Button>
      </Space>
    </Form>
  );
};
